import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import Hls from "hls.js";
import "./customLivePagePlayer.css";
import { ProgressSpinner } from "primereact/progressspinner";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { toast } from "../../components/Toast/toast.ts";
import { TimelineWrapper } from "./Timeline/TimelineWrapper.tsx";
import parseTimelineValue from "../utils/dateTime/parseTimelineValue.ts";
import styles from "../LiveViewPageTrial/Timeline/VideoPlayback.module.scss";
import Forward10Icon from "@mui/icons-material/Forward10";
import Replay10Icon from "@mui/icons-material/Replay10";
import { VideoJS } from "../../components/VideoJS/VideoJS.tsx";
import type { VideoJSHandle } from "./VideoJS/VideoJS.tsx";
import { DATE_TIME_FORMAT } from "../utils/dateTime/const.ts";
import { CustomDatePicker } from "./DatePicker/DatePicker.tsx";
import { useUserContext } from "../../context/UserContext.tsx";
import { addDays, endOfDay, format, isBefore, startOfDay } from "date-fns";
import LightTooltip from "../../components/CustomTooltip.tsx";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { PtzParameters } from "../stores/types.ts";
import { PosOverlay, POSTransaction } from "./PosOverlay/PosOverlay.tsx";
import { useTimelineTransactions } from "../hooks/useTimelineTransactions.ts";
import { usePlaybackTransactions } from "../hooks/usePlaybackTransactions.ts";
import { unstable_batchedUpdates } from "react-dom";
import { formatInTimeZone } from "date-fns-tz";    
import { useMediaQuery } from '@mui/material';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import { createVideoConnection } from "../utils/video/videoConnection.tsx";

export interface ITransform {
  scale: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PanZoomParameters {
  x: number;
  y: number;
  scale: number;
  width: number;
  height: number;
}

const PlayerWithTimeline = ({
  src,
  highSrc,
  cameraId,
  isFisheye,
  timeZone,
  remoteUrl,
  posCamera,
}) => {
  const [muted, setMuted] = useState(true);
  const isPreloadingNext = useRef(false);
  const [recordingSrc, setRecordingSrc] = useState<string | undefined>();
  const [loading, setLoading] = useState(true); // show spinner until high stream plays

  // The modal always uses the high stream — its own connection, its own video element.
  // The grid tile's low stream is never touched, so it never blacks out.
  // activeLiveSrc starts as highSrc. If highSrc isn't available yet, fall back to src.
  const [activeLiveSrc, setActiveLiveSrc] = useState<string>(highSrc || src);
  const highStreamUpgradedRef = useRef(false);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [paused, setPaused] = useState(false);
  const [manualShort, setManualShort] = useState(false);
  const [manualLong, setManualLong] = useState(false);
  const seekStartRef = useRef<number>(0);
  const seekIndexRef = useRef(0);
  const itemRef = useRef<TimelineItem | null>(null);
  const canUpdateTime = useRef<boolean>(true);
  const [playbackBeforeSeek, setPlaybackBeforeSeek] = useState(false);
  const seekPosRef = useRef(Date.now());
  const needSeekAfterLoad = useRef<number | false>(false);
  const [liveDisplayed, setLiveDisplayed] = useState(Boolean(true));
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const liveVideoJsRef = useRef<VideoJSHandle | null>(null);
  const videoJsRef = useRef<VideoJSHandle | null>(null);
  const lastLoggedMinuteRef = useRef<number | null>(null);
  const [loadingRec, setLoadingRec] = useState(false);
  // const isRecordingPlaying = Boolean(recordingSrc);
  const playbackOn = Boolean(recordingSrc);
  const [playbackVisible, setPlaybackVisible] = useState(true);
  const [cursorSeekTime, setCursorSeekTime] = useState(Date.now());
  const [fullscreenSource, setFullscreenSource] = useState<
    "none" | "live" | "playback">("none");
  const { globalStoreData } = useUserContext();
  const isSeekingRef = useRef(false);
  const [showTimeline, setShowTimeline] = useState(true);
  // const livePTZRef = useRef(false);
  // const playbackPTZRef = useRef(false);
  const [isLivePTZ, setIsLivePTZ] = useState(false);         // Tracks Live 360° status
  const [isPlaybackPTZ, setIsPlaybackPTZ] = useState(false); // Tracks Playback 360° status
  //  const [isFullscreen, setIsFullscreen] = useState(false);
  const suppressCursorUpdate = useRef(false);
  const [, forceRender] = useState(false); // only if UI needs to show status
  const ptzLiveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ptzPlaybackCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const livePtzParametersRef = useRef<PtzParameters | null>(null);
  const playbackPtzParametersRef = useRef<PtzParameters | null>(null);
  const [showLiveTx, setShowLiveTx] = useState<boolean>(true);
  const [showPlaybackTx, setShowPlaybackTx] = useState<boolean>(true);
  const [currentTimestamp, setCurrentTimestamp] = useState<number | null>(null);
  const isMobileScreen = useMediaQuery('(max-width: 600px)');
  const [isRotateHintDismissed, setIsRotateHintDismissed] = useState(false);

  const [livePOSTransactions, setLivePOSTransactions] = useState<
    Record<string, POSTransaction>>({});
  const {
    timelineTransactions,
    fetchTimelineTransactions,
    resetTimelineTransactions,
  } = useTimelineTransactions();

  const stableFetchTimelineTransactions = useCallback(fetchTimelineTransactions, []);
  const stableResetTimelineTransactions = useCallback(resetTimelineTransactions, []);

  const { currentPOSTransactions: playbackPOSTransactions } =
    usePlaybackTransactions({
      cameraId,
      currentTimestamp,
      playbackOn,
    });

  const playbackTransformRef = useRef<ITransform>({
    scale: 1,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const handleDisplayLiveTransactions = () => {
    setShowLiveTx((prev) => !prev);
  };

  // ---- HIGH STREAM CONNECTION ----
  // Pre-create the high stream connection immediately so it starts negotiating.
  // activeLiveSrc is already set to highSrc, so VideoJS will pick it up directly.
  useEffect(() => {
    if (!highSrc || !cameraId) return;
    highStreamUpgradedRef.current = false;
    setActiveLiveSrc(highSrc);
    // Pre-warm: createVideoConnection is idempotent (returns cached if exists)
    createVideoConnection(highSrc, cameraId);
  }, [highSrc, cameraId]);

  const handleDisplayPlaybackTransactions = () => {
    setShowPlaybackTx((prev) => !prev);
  };

  useEffect(() => {
    if (!recordingSrc) {
      setLiveDisplayed(true);
      setPlaybackVisible(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      setIsFullscreen(isFullscreenNow);

      if (!isFullscreenNow) {
        // 👇 Fullscreen exited — restore normal state
        setFullscreenSource("none");

        // Restore based on current mode
        if (recordingSrc) {
          setLiveDisplayed(false);
          setPlaybackVisible(true);
        } else {
          setLiveDisplayed(true);
          setPlaybackVisible(false);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, [recordingSrc]);

  useEffect(() => {
    if (!recordingSrc) {
      setLiveDisplayed(true);
      setPlaybackVisible(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      // Full cleanup
      setRecordingSrc(undefined);
      setPlaybackVisible(false);
      setLiveDisplayed(true);
      setLoadingRec(false);

      if (videoJsRef.current?.destroy) {
        videoJsRef.current.destroy();
        videoJsRef.current = null;
      }

      // 👇 Critical if live video (videoJsRef) still holds stale video
      if (liveVideoJsRef.current?.video) {
        liveVideoJsRef.current.video.srcObject = null;
        liveVideoJsRef.current.video.src = "";
      }
    };
  }, []);

  const now = Date.now();

  const [liveFormattedDate, liveFormattedTime] = useMemo(() => {
    const time = formatInTimeZone(now, timeZone, DATE_TIME_FORMAT);
    return time.replaceAll("/", ".").split(" ");
  }, [now]);

  const [seekFormattedDate, seekFormattedTime] = useMemo(() => {
    const seekTime = formatInTimeZone(
      seekPosRef.current,
      timeZone,
      DATE_TIME_FORMAT
    );
    return seekTime.replaceAll("/", ".").split(" ");
  }, [seekPosRef.current, timeZone]);

  const handleLiveClick = useCallback(() => {
    if (!recordingSrc && liveDisplayed) {
      toast.info("Already in live mode.");
      return;
    }

    setLiveDisplayed(true);

    // Destroy playback
    if (videoJsRef.current?.destroy) {
      videoJsRef.current.destroy();
      videoJsRef.current = null;
    }

    setRecordingSrc(undefined);
    setPlaybackVisible(false);
    setLoadingRec(false);

    // Reset fisheye PTZ when going live
    if (isFisheye) {
      setIsLivePTZ(false); // always start in 360 view
    }

    const videoElement = liveVideoJsRef.current?.video;
    const isAlreadyPlaying =
      videoElement &&
      videoElement.readyState > 2 &&
      !videoElement.paused &&
      !videoElement.ended;
    //Only show the spinner if the live video isn't already playing.If it is, ensure the spinner is turned off.
    setLoading(!isAlreadyPlaying);
  }, [liveDisplayed, recordingSrc, setRecordingSrc, isFisheye]);

  const handleLoadedLive = useCallback((e: Event) => {
    if (e.currentTarget instanceof HTMLVideoElement && liveVideoJsRef.current) {
      liveVideoJsRef.current.video = e.currentTarget;
    }
  }, []);

  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    if (!cameraId) return;
    handleGetTimeline(cameraId);
  }, [cameraId]);

  const stableTimeline = useMemo(() => timeline, [timeline]);

  useEffect(() => {
    const isPosMatch = posCamera && posCamera === cameraId;

    if (!isPosMatch) {
      setLivePOSTransactions({});      // clear POS overlays
      return; // no SSE
    }
    //const remote__Url = "http://arthurst7-11.myliveeye.com:8000";
    const eventSource = new EventSource(`${remoteUrl}/v1/pos/sse`);
    //console.log("eventSource", eventSource)

    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        if (payload.transaction) {
          // 👇 Ignore cameraId filter for now
          setLivePOSTransactions((prev) => {
            const updated = {
              ...prev,
              [payload.transaction.posId]: payload.transaction,
            };
            //console.log("[UPDATED POS TRANSACTIONS STATE]", updated);
            return updated;
          });
        }
        // else {
        //   console.log("[SSE NON-TRANSACTION]", payload);
        // }
      } catch (err) {
        console.error(`[useSSE] Cannot parse data: ${event.data} ${err}`);
      }
    };

    eventSource.onerror = (e) => {
      console.error("[useSSE] Connection error", e);
    };

    // eventSource.onopen = () => {
    //   console.log("[useSSE] Connected");
    // };

    return () => {
      eventSource.close();
    };
  }, [cameraId, posCamera, remoteUrl]);

  const handleGetTimeline = async (_id: any, limit?: number) => {
    //if (!globalStoreData) return;

    //const remoteUrl = "http://broadwaycornermarket.myliveeye.com:8000";
    // const remoteUrl = globalStoreData?.configs?.visen_ai?.remote_url;
    if (!remoteUrl) return;

    try {
      const response = await axios.get(
        `${remoteUrl}/v1/recordings/timeline/${_id}${limit ? `?limit=${limit}` : ""
        }`
      );
      if (response.status === 200) {
        const data = response.data.success.timeline;
        setTimeline(data);
      }
    } catch (error: any) {
      console.error("Failed to fetch timeline:", error);
      if (axios.isAxiosError(error)) {
        toast.error(
          `Timeline request failed: ${error.response?.status || "unknown error"
          }`
        );
        setLoading(false);
      } else {
        toast.error(
          "An unexpected error occurred while fetching the timeline."
        );
      }
    }
  };

  const handleLoad = useCallback(
    (seekPos: number, forceLoad = false) => {
      //if (!globalStoreData) return;

      //const remoteUrl = globalStoreData?.configs?.visen_ai?.remote_url;
      // const remoteUrl = "https://broadwaycornermarket.myliveeye.com:8002";
      if (!remoteUrl) return;

      // 1. Find the correct file in the timeline
      let idx = timeline.findLastIndex((i) => i.start <= seekPos && (i.end === "live" || seekPos < i.end));
      if (idx < 0) idx = timeline.findIndex((i) => i.end === "live" || seekPos < i.end);
      if (idx < 0) {
        console.log("No file covers this time", { seekPos });
        return;
      }

      seekIndexRef.current = idx;
      itemRef.current = timeline[idx];
      const { start, end, fileName } = itemRef.current;

      // 2. Calculate offset in seconds
      let offset = Math.max(0, Math.floor((seekPos - start) / 1000));
      // Clamp offset to file duration
      let fileDuration = (end === "live" ? Date.now() : end) - start;
      if (offset > Math.floor(fileDuration / 1000))
        offset = Math.floor(fileDuration / 1000);

      // 3. Only change src if new file
      const playlistPath = fileName.replace(/\\/g, "/");

      //const newUrl = `https://app-dev.myliveeye.com/visen/static/recordings/${playlistPath}?siteId=6757f87b508fbac1a9907724`
      const newUrl = `${remoteUrl}/static/recordings/${playlistPath}`;

      let shouldReload = recordingSrc !== newUrl || forceLoad;

      if (shouldReload) {
        suppressCursorUpdate.current = true; // block cursor updates from RAF
        seekStartRef.current = start;
        needSeekAfterLoad.current = offset; // seconds
        seekPosRef.current = seekPos;

        unstable_batchedUpdates(() => {
          setPlaybackBeforeSeek(true); // spinner
          setRecordingSrc(newUrl);
          setCursorSeekTime(seekPos);
          setCurrentTimestamp(seekPos);
        });
      } else {
        if (videoRef.current) {
          videoRef.current.currentTime = offset;
        }
        seekPosRef.current = seekPos;
        setCursorSeekTime(seekPos);
        setCurrentTimestamp(seekPos);
        canUpdateTime.current = true;
        return;
      }
    },
    [timeline, src, playbackBeforeSeek, isFisheye]
  );

  const handleLoadNext = useCallback(
    (event?: any) => {
      try {
        const isEnded = event?.type === "ended";
        const isError = event?.type === "error" || event === "MediaError";
        const isBufferStall = event?.details === "bufferStalledError";

        setManualLong(false);
        setManualShort(false);

        const item = itemRef.current;
        const currentPos = seekPosRef.current;
        const index = seekIndexRef.current;
        const nextItem = timeline[index + 1];

        if (!item) return;

        // Handle live
        if (item.end === "live" && videoJsRef.current && !isError) {
          // @ts-ignore
          videoJsRef.current.loadLastRecording();
          needSeekAfterLoad.current = currentPos - item.start;
          return;
        }

        const endTime = parseTimelineValue(item.end);
        const timeLeft = endTime - currentPos;

        let pos: number | null = null;

        // Load next only if it's truly ended or buffer stalled near the end
        if (
          (isEnded || (isBufferStall && timeLeft < 3000)) &&
          nextItem?.start
        ) {
          pos = nextItem.start;
        }

        if (pos != null) {
          handleLoad(pos, true);
        } else {
          seekPosRef.current = Date.now();
          setRecordingSrc(undefined);
        }
      } finally {
        isPreloadingNext.current = false;
      }
    },
    [handleLoad, timeline]
  );

  const handleTimeUpdate = useCallback(
    (e: Event) => {
      const { currentTarget } = e;
      if (suppressCursorUpdate.current) return; //  skip premature update
      if (!(currentTarget instanceof HTMLVideoElement)) return;

      const video = currentTarget;
      const globalPosMs = seekStartRef.current + video.currentTime * 1000;

      if (
        globalPosMs &&
        needSeekAfterLoad.current === false &&
        !playbackBeforeSeek &&
        canUpdateTime.current
      ) {
        seekPosRef.current = globalPosMs;
        setCursorSeekTime(globalPosMs);
        setCurrentTimestamp(globalPosMs);
      }

      const currentPos = seekPosRef.current;
      const item = itemRef.current;
      const endTime = parseTimelineValue(item?.end);
      const timeLeft = endTime - currentPos;

      if (timeLeft < 5000 && !isPreloadingNext.current) {
        isPreloadingNext.current = true;
        //handleLoadNext(); // load or preload next chunk
      }

      // 👇 ADD THIS TO LOG EACH MINUTE
      const currentMinute = Math.floor(globalPosMs / (60 * 1000)); // minute in ms
      if (lastLoggedMinuteRef.current !== currentMinute) {
        lastLoggedMinuteRef.current = currentMinute;

        const timeStr = new Date(globalPosMs).toLocaleTimeString();
      }
      // TODO preload next chunk
    },
    [handleLoadNext, playbackBeforeSeek]
  );

  const hasSeekedRef = useRef(false);

  const handleSeekIfNeeded = useCallback(() => {
    try {
      if (needSeekAfterLoad.current !== false && !hasSeekedRef.current) {
        const seekTime = needSeekAfterLoad.current;
        if (videoJsRef.current?.seek) {
          videoJsRef.current.seek(seekTime);
        } else {
          console.warn(
            "Ref does not expose a seek function!",
            videoJsRef.current
          );
        }

        needSeekAfterLoad.current = false;
        suppressCursorUpdate.current = false;
      }

      // ✅ Reset seek lock so next seek can proceed
      isSeekingRef.current = false;
    } catch (err) {
      console.error("handleSeekIfNeeded error:", err);
    }
  }, []);

  const handleCanPlayThrough = useCallback(() => {
    if (!videoJsRef.current || typeof videoJsRef.current.seek !== "function") {
      console.warn("Cannot call seek in canPlayThrough, ref not ready.");
      return;
    }
    const item = itemRef.current;
    if (item?.end !== "live") {
      handleSeekIfNeeded();
    }
  }, [handleSeekIfNeeded]);

  const handleLoaded = useCallback(() => {
    try {
      const video = videoJsRef.current?.video;
      const item = itemRef.current;

      if (!video) {
        console.warn("Playback video element is not available.");
        return;
      }

      if (item?.end === "live") {
        handleSeekIfNeeded();
      }

      if (paused) {
        video.pause();
      } else {
        if (video.src && document.body.contains(video)) {
          video.play().catch((err) => {
            console.warn("Autoplay failed on load:", err);
          });
        }
      }

      canUpdateTime.current = true;
    } catch (err) {
      console.error("handleLoaded error:", err);
    }
  }, [paused, handleSeekIfNeeded]);

  const handleTogglePlay = useCallback(() => {
    try {
      const video = videoJsRef.current?.video;
      if (!video) {
        console.warn("handleTogglePlay: video is null or not ready");
        return;
      }

      setPaused((prev) => {
        try {
          if (prev) {
            video.play().catch((err) => console.warn("Play failed:", err));
          } else {
            video.pause();
          }
        } catch (err) {
          console.error("TogglePlay error:", err);
        }
        return !prev;
      });
    } catch (err) {
      console.error("handleTogglePlay outer error:", err);
    }
  }, []);

  const handleSeek = useCallback(
    (position: number) => {

      setLiveDisplayed(false);
      setPlaybackVisible(true);

      handleLoad(position, false); // let handleLoad handle spinner + cursor update
    },
    [handleLoad]
  );

  const handleSeekLong = useCallback(
    (pos: number) => {

      unstable_batchedUpdates(() => {
        setManualShort(false);
        setManualLong(true);
        setLiveDisplayed(false);
        setPlaybackVisible(true);

        if (liveVideoJsRef.current?.video) {
          liveVideoJsRef.current.video.srcObject = null;
          liveVideoJsRef.current.video.src = "";
        }

        handleSeek(pos);
      });
    },
    [handleSeek]
  );

  useEffect(() => {
    let rafId: number | null = null;

    const updateCursor = () => {
      const videoWrapper = videoJsRef.current;
      const video = videoWrapper?.video;

      // ✅ Priority 1: if recording is playing, use playback time
      // 🔥 Always update cursor when playback video is running
      if (
        video &&
        typeof video.currentTime === "number" &&
        !suppressCursorUpdate.current
      ) {
        const playbackTime = seekStartRef.current + video.currentTime * 1000;
        seekPosRef.current = playbackTime;
        setCursorSeekTime(playbackTime);
      }

      // ✅ Priority 2: fallback to live time only if not playing recording
      else if (liveDisplayed) {
        const liveTime = Date.now();
        seekPosRef.current = liveTime;
        setCursorSeekTime(liveTime);
      }

      rafId = requestAnimationFrame(updateCursor);
    };

    rafId = requestAnimationFrame(updateCursor);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [liveDisplayed, playbackOn]);

  const handleHlsError = useCallback(
    (hlsEvent?: any, data?: any, hlsRef?: any) => {
      if (data.fatal) {
        switch (data.type) {
          case hlsRef.ErrorTypes.NETWORK_ERROR:
          
            console.warn(" Attempting network recovery");
            hlsRef.startLoad();
            break;

          case hlsRef.ErrorTypes.MEDIA_ERROR:
            // Try to recover media error (e.g., decode or buffer)
            console.warn(" Attempting media recovery");
            hlsRef.recoverMediaError();
            break;

          default:
            console.error("❌ Fatal error, destroying HLS instance");
            hlsRef.destroy();
            break;
        }
      } else if (data.details === "bufferStalledError") {
        console.warn("🧪 Buffer stalled, attempting to preload next segment");
        isPreloadingNext.current = true;
        videoRef?.current?.pause();
        setTimeout(() => {
          videoRef?.current?.play()?.catch((err) => {
            if (
              err?.message?.includes("The play() request was interrupted") ||
              err?.name === "AbortError"
            ) {
              console.warn("[Suppressed play() interruption]:", err.message);
              return;
            }

            console.error("Unhandled error while trying to resume video:", err);
          });
        }, 500);
      }
    },
    []
  );

  const handleMetaDataLoaded = useCallback(
    (_event: any, _data: any, hlsRef: Hls) => {
      const video = videoRef.current;
      if (!video) return;

      // If user clicked on the timeline and seek time was stored
      if (typeof needSeekAfterLoad.current === "number") {
        const seekTime = needSeekAfterLoad.current;

        // Start loading from the desired time
        hlsRef.startLoad(seekTime);

        // Ensure the video element reflects the seek time
        video.currentTime = seekTime;

        // Reset flag
        needSeekAfterLoad.current = false;
      }
    },
    []
  );

  const handleCanPlay = useCallback((e: Event) => {
    if (e.currentTarget instanceof HTMLVideoElement) {
      const video = (videoRef.current = e.currentTarget);
      video.play().catch((err) => {
        console.warn("Autoplay failed in onCanPlay:", err);
      });
      setPlaybackBeforeSeek(false);
      canUpdateTime.current = true;
      //  extra safeguard: ensure cursor resumes
      suppressCursorUpdate.current = false;
    }
  }, []);

  const handleForward10 = useCallback(() => {
    const item = itemRef.current;
    if (!item) return;

    const end = item.end === "live" ? Date.now() : item.end;
    const maxForwardPos = end - 1000; // leave 1s buffer before end
    const newPos = Math.min(seekPosRef.current + 10_000, maxForwardPos);

    seekPosRef.current = newPos;
    const offsetSec = (newPos - seekStartRef.current) / 1000;

    if (videoJsRef.current?.seek) {
      videoJsRef.current.seek(offsetSec);
    }
  }, []);

  const handleRewind10 = useCallback(() => {
    const newPos = Math.max(seekStartRef.current, seekPosRef.current - 10_000);
    seekPosRef.current = newPos;
    const offsetSec = (newPos - seekStartRef.current) / 1000;
    if (videoJsRef.current?.seek) {
      videoJsRef.current.seek(offsetSec);
    }
  }, []);

  const includedDates = useMemo(() => {
    const dates = new Set<Date>();
    for (const item of timeline) {
      const { start, end } = item;
      const parsedEnd = parseTimelineValue(end);
      for (
        let date = startOfDay(start);
        isBefore(date, endOfDay(parsedEnd));
        date = addDays(date, 1)
      ) {
        dates.add(date);
      }
    }
    dates.add(new Date());

    return Array.from(dates);
  }, [timeline]);


  const calendarButton = useMemo(
    () => (
      <CustomDatePicker
        selected={new Date(seekPosRef.current)}
        handleChange={(date) => {
          if (!date) return;

          const normalized = startOfDay(date).getTime() + 1;

          // ⛔ Prevent double-seek
          if (seekPosRef.current === normalized) return;

          // ⛔ Temporarily disable timeline auto-normalization
          setManualLong(true);
          setManualShort(true);

          seekPosRef.current = normalized;
          handleSeek(normalized);

          // Re-enable after 300ms
          setTimeout(() => {
            setManualLong(false);
            setManualShort(false);
          }, 300);
        }}
        includedDates={includedDates}
        rightLabel="Calendar"
      />
    ),
    [includedDates, handleSeek]
  );

  const handleToggleTimelineVisibility = () => {
    setShowTimeline((prev) => !prev);
  };

  // SINGLE FULLSCREEN HANDLER ---
  const handleToggleFullscreen = async () => {
    const container = document.getElementById("video-box");
    if (!container) return;

    if (isIOS) {
      // ⛔ IMPORTANT FIX: Prevent fullscreen during PLAYBACK on iPhone
      if (recordingSrc) {
        // playback is active → iOS fullscreen must be blocked
        return;
      }
      if (container.classList.contains("ios-force-fullscreen")) {
        // --- EXITING ---
        container.classList.remove("ios-force-fullscreen");
        document.body.classList.remove("fullscreen-active");

        // Improvement: Re-enable scrolling on HTML
        document.documentElement.style.overflow = "";

        setIsFullscreen(false);
      } else {
        // --- ENTERING ---
        container.classList.add("ios-force-fullscreen");
        document.body.classList.add("fullscreen-active");

        // Improvement: Force lock scrolling on HTML element too
        document.documentElement.style.overflow = "hidden";

        setIsFullscreen(true);
        setIsRotateHintDismissed(false);
      }
      return;
    }
    // STRATEGY 2: ANDROID / PC (Native API)
    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
        // Android Landscape Lock
        if (window.screen?.orientation?.lock) {
          await window.screen.orientation.lock("landscape").catch(() => { });
        }
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn("Fullscreen toggle error:", err);
    }
  };

  // --- B. LISTENER FOR ANDROID BACK BUTTON ---
  // Replace your existing fullscreen listener useEffect with this:
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement || // TypeScript might complain, can cast as any if needed
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      setIsFullscreen(isFullscreenNow);

      // If native fullscreen exits (Android/Desktop), reset specific UI states
      if (!isFullscreenNow && !isIOS) {
        // Logic to ensure UI is clean
      }
    };

    const events = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "msfullscreenchange"
    ];

    events.forEach(evt => document.addEventListener(evt, handleFullscreenChange));

    return () => {
      events.forEach(evt => document.removeEventListener(evt, handleFullscreenChange));
    };
  }, []); // <--- Dependency array is empty now. Stable.

  // NEW: Listen for orientation changes to auto-dismiss the hint
  useEffect(() => {
    const handleOrientationChange = () => {
      // Check if landscape
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;

      // If user obeyed the hint and rotated, permanently dismiss it for this session
      if (isLandscape && isFullscreen) {
        setIsRotateHintDismissed(true);
      }
    };

    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, [isFullscreen]);

  // --- C. FISHEYE HANDLERS (Updated to use State + Fullscreen) ---
  const handleFisheyeLiveClick = () => {
    setIsLivePTZ(prev => !prev); // Toggle State
    if (isMobileScreen || isIOS) {
      handleToggleFullscreen();
    }
  };

  const handleFisheyePlaybackClick = () => {
    setIsPlaybackPTZ(prev => !prev); // Toggle State
    if (isMobileScreen || isIOS) {
      handleToggleFullscreen();
    }
  };

  return (
    <div
      className="custom-video-player"
      id="video-box"
      style={{
        backgroundColor: "black",
        width: "100%",
      }}
      ref={containerRef}
    >
      {/* Only show if NOT dismissed */}
      {!isRotateHintDismissed && (
        <div className="rotate-hint-overlay">
          <ScreenRotationIcon style={{ fontSize: 50, marginBottom: 15 }} />
          <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
            Rotate for Better View
          </span>

          {/* Buttons Container */}
          <div style={{ marginTop: 25, display: "flex", gap: "15px" }}>

            {/* Option A: Dismiss / Close */}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent click from pausing video
                setIsRotateHintDismissed(true);
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: "#333",
                color: "white",
                border: "1px solid white",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "0.9rem"
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {liveDisplayed && loading && (
        <div
          className="loading-spinner"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
          }}
        >
          <ProgressSpinner
            style={{ width: "60px", height: "60px", color: "white" }}
          />
        </div>
      )}
      <div className="video-player-container">
        <div
          style={{
            display: recordingSrc && playbackVisible ? "block" : "none",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "98%",
          }}
        >
          {recordingSrc && playbackVisible && (
            <>
              {loadingRec && (
                <div className="loading-spinner">
                  <ProgressSpinner
                    style={{ width: "60px", height: "60px", color: "white" }}
                  />
                </div>
              )}
              <div
                className={styles.playback}
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "10px",
                  right: "10px",
                  display: "flex",
                  flexWrap: isMobileScreen ? "wrap" : "nowrap",
                  justifyContent: "space-between",
                  gap: "20px",
                  zIndex: 2, // ensure above video
                  padding: isMobileScreen ? "0px" : "4px 8px",
                  borderRadius: "4px",
                }}
              >
                <span className={styles.playbackText}>PLAYBACK</span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <span
                    className={`POSButton ${showPlaybackTx ? "active" : ""} `}
                    onClick={handleDisplayPlaybackTransactions}
                  >
                    POS
                  </span>
                  {isFisheye && playbackVisible && (
                    <span
                      onClick={handleFisheyePlaybackClick}
                      className={`POSButton ${isPlaybackPTZ ? "" : "active"
                        } `}
                    >
                      360°
                    </span>
                  )}
                  <div className={styles.overlayLabelsBlock}>
                    {calendarButton}
                  </div>
                  <span className={styles.playbackTime}>
                    <span>
                      {seekFormattedDate} {seekFormattedTime}
                    </span>
                  </span>
                  <LightTooltip title="Expand" placement="top" arrow>
                    <span>
                      {!(isIOS && recordingSrc) && (
                        <FullscreenIcon
                          sx={{
                            color: "white",
                            cursor: "pointer",
                            backgroundColor: "#1d2122",
                            borderRadius: "5px",
                          }}
                          onClick={handleToggleFullscreen}
                        />
                      )}
                    </span>
                  </LightTooltip>
                </div>
              </div>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                <TransformWrapper
                  disabled={isFisheye && isPlaybackPTZ}
                  wheel={{ disabled: false }}
                >
                  <TransformComponent>
                    <VideoJS
                      src={recordingSrc}
                      isLive={false}
                      isLoading={loadingRec}
                      setIsLoading={setLoadingRec}
                      splitView={false}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={handleLoadNext}
                      onError={handleHlsError}
                      onMetaDataLoaded={handleMetaDataLoaded}
                      onCanPlay={handleCanPlay}
                      onCanPlayThrough={handleCanPlayThrough}
                      onLoaded={handleLoaded}
                      ref={videoJsRef}
                      videoRef={videoRef}
                      ptzOn={isPlaybackPTZ}
                      fisheye={isFisheye}
                      muted={muted}
                      playingRecordingRef={itemRef}
                      lastRecordingLoading={playbackBeforeSeek}
                      setLastRecordingLoading={setPlaybackBeforeSeek}
                      cameraId={cameraId}
                      ptzParametersRef={playbackPtzParametersRef}
                      transformRef={playbackTransformRef}
                      ptzCanvasRef={ptzPlaybackCanvasRef}
                    />
                  </TransformComponent>
                </TransformWrapper>
                {playbackVisible && showPlaybackTx && (
                  <PosOverlay
                    currentPOSTransactions={playbackPOSTransactions ?? {}}
                    className={styles.posOverlay}
                  />
                )}
              </div>
            </>
          )}
        </div>
        <div
          style={{
            display: liveDisplayed ? "flex" : "none",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "98%",
          }}
          className={styles.liveContainer}
        >
          {liveDisplayed && (
            <div
              className={styles.liveInfo}
              style={{
                position: "absolute",
                bottom: "10px", // adjust as needed
                left: "10px",
                right: "10px",
                display: "flex",
                flexWrap: isMobileScreen ? "wrap" : "nowrap",
                padding: isMobileScreen ? "0px" : "4px 8px",
                justifyContent: "space-between",
                gap: "20px",
                zIndex: 2, // ensure above video
                borderRadius: "4px",
              }}
            >
              <span
                className={styles.liveText}
                style={{ backgroundColor: "red" }}
              >
                LIVE
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <span
                  className={`POSButton ${showLiveTx ? "active" : ""} `}
                  onClick={handleDisplayLiveTransactions}
                >
                  POS
                </span>
                {isFisheye && liveDisplayed && (
                  <span
                    onClick={handleFisheyeLiveClick}
                    className={`POSButton ${isLivePTZ ? "" : "active"
                      } `}
                  >
                    360°
                  </span>
                )}
                {!recordingSrc && (
                  <div className={styles.overlayLabelsBlock}>
                    {calendarButton}
                  </div>
                )}
                <span className={styles.liveTime}>
                  <span>
                    {liveFormattedDate} {liveFormattedTime}
                  </span>
                </span>
                <LightTooltip title="Expand" placement="top" arrow>
                  <span>
                    <FullscreenIcon
                      sx={{
                        color: "white",
                        cursor: "pointer",
                        backgroundColor: "#1d2122",
                        borderRadius: "5px",
                      }}
                      onClick={handleToggleFullscreen}
                    />
                  </span>
                </LightTooltip>
              </div>
            </div>
          )}
          <TransformWrapper
            disabled={isFisheye && isLivePTZ}
            wheel={{ disabled: false }}
          >
            <TransformComponent>
              <VideoJS
                src={activeLiveSrc}
                isLoading={loading}
                setIsLoading={setLoading}
                isLive={true}
                onLoaded={handleLoadedLive}   
                ref={liveVideoJsRef}
                ptzOn={isLivePTZ}
                fisheye={isFisheye}
                onLoadedMetadata={() => {
                  setLoading(false); // metadata available = safe to hide spinner
                }}
                onCanPlay={() => {
                  setLoading(false); // extra fallback
                }}
                onCanPlayThrough={() => {
                  setLoading(false); // extra fallback
                }}
                onPlaying={() => {
                  setLoading(false); // video is actively playing
                }}
                videoRef={liveVideoRef}
                cameraId={cameraId}
                splitView={false}
                ptzParametersRef={livePtzParametersRef}
                transformRef={playbackTransformRef}
                ptzCanvasRef={ptzLiveCanvasRef}
              />
            </TransformComponent>
          </TransformWrapper>
          {liveDisplayed && showLiveTx && (
            <PosOverlay
              currentPOSTransactions={livePOSTransactions}
              className={styles.posOverlay}
            />
          )}
        </div>
      </div>
      <div
        className="updated-controls-bottom-container"
        id="livePageControlsBottomContainer"
      >
        <div
          className="controls-left-container"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isMobileScreen ? "35px" : "28px",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              flexDirection: "row",
              gap: "5px",
            }}
          >
            <span
              className="LiveText"
              style={{
                color: "white",
                cursor: "pointer",
                backgroundColor: "#1d2122",
                padding: "5px 8px",
                marginLeft: "11px",
              }}
              onClick={handleToggleTimelineVisibility}
            >
              {showTimeline ? "Hide Timeline" : "Show Timeline"}
            </span>
          </div>
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            {playbackVisible && (
              <>
                <LightTooltip title="Rewind 10 seconds" placement="top" arrow>
                  <button
                    className={styles.controlButton}
                    onClick={handleRewind10}
                  >
                    <Replay10Icon style={{ fill: "white" }} />
                  </button>
                </LightTooltip>
                <LightTooltip
                  title={paused ? "Play" : "Pause"}
                  placement="top"
                  arrow
                >
                  <button
                    onClick={handleTogglePlay}
                    className={styles.controlButton}
                    style={{ border: "none" }}
                  >
                    {paused ? (
                      <PlayArrowIcon sx={{ color: "white" }} />
                    ) : (
                      <PauseIcon sx={{ color: "white" }} />
                    )}
                  </button>
                </LightTooltip>
                <LightTooltip title="Forward 10 seconds" placement="top" arrow>
                  <button
                    className={styles.controlButton}
                    onClick={handleForward10}
                  >
                    <Forward10Icon style={{ fill: "white" }} />
                  </button>
                </LightTooltip>
              </>
            )}
          </div>
          <div style={{ flex: "0 0 auto" }}>
            {playbackVisible && (
              <span
                className="LiveText"
                style={{
                  cursor: "pointer",
                  padding: "5px",
                  backgroundColor: "red",
                  color: "white",
                  borderRadius: "5px",
                  marginRight: "5px",
                }}
                onClick={handleLiveClick}
              >
                Go Live
              </span>
            )}
          </div>
        </div>
        <div style={{ display: showTimeline ? 'block' : 'none', width: '100%' }} className="mb-4">
          <TimelineWrapper
            timeline={stableTimeline}
            timeZone={timeZone}
            onSeek={handleSeekLong}
            seekPosRef={seekPosRef}
            showTimeline={showTimeline}
            short={false}
            playbackOn={playbackOn}
            currentSeekTime={cursorSeekTime}
            onSetManual={setManualLong}
            manualPosition={manualLong}
            timelineItemRef={itemRef}
            cameraId={cameraId}
            posOn={true}
            posCamera={posCamera}
            fetchTimelineTransactions={stableFetchTimelineTransactions}
            timelineTransactions={timelineTransactions}
            resetTimelineTransactions={stableResetTimelineTransactions}
          />
        </div>
      </div>
    </div>
  );
};

export default PlayerWithTimeline;